import { useEffect, useRef, useState } from 'react';
import { Centrifuge } from 'centrifuge';
import { useAppDispatch } from '../redux';
import { updateEvents } from '../../store/slices/mapSlice';

interface UseCentrifugoProps {
    connectionToken: string;
    cityTag: string;
    isPushEnabled?: boolean;
    isMailingEnabled?: boolean;
}

export const useCentrifugo = ({ connectionToken, cityTag, isPushEnabled = true, isMailingEnabled = true }: UseCentrifugoProps) => {
    const dispatch = useAppDispatch();
    const centrifugeRef = useRef<Centrifuge | null>(null);
    const [isConnected, setIsConnected] = useState(false);
    const subscriptionRef = useRef<any>(null);
    useEffect(() => {

        if (!isPushEnabled) {
            console.log('🔌 Push уведомления отключены, WebSocket не подключается');
            setIsConnected(false);
            return;
        }

        if (!connectionToken || !cityTag) return;

        console.log('🔌 Подключение к Centrifugo для города:', cityTag);


        const centrifuge = new Centrifuge('wss://ws.dpscontrol.ru/connection/websocket', {
            token: connectionToken
        });

        centrifugeRef.current = centrifuge;

        // Обработчики событий соединения
        centrifuge.on('connected', () => {
            console.log('✅ Подключен к Centrifugo');
            setIsConnected(true);
        });

        centrifuge.on('disconnected', () => {
            console.log('🔌 Отключен от Centrifugo');
            setIsConnected(false);
        });

        centrifuge.on('error', (ctx) => {
            console.error('❌ Ошибка Centrifugo:', ctx);
            setIsConnected(false);
        });

        // Подписка на канал города
        const subscription = centrifuge.newSubscription("$" + cityTag, {
            getToken: async (ctx: any) => {
                const initData = window.Telegram?.WebApp?.initData || import.meta.env.VITE_DEVELOP_MODE || '';

                console.log('🔐 Запрос токена подписки для канала:', ctx.channel, 'initData:', initData ? 'установлен' : 'отсутствует');

                const res = await fetch('https://dpscontrol.ru/webapp/token', {
                    method: 'POST',
                    headers: new Headers({
                        'Content-Type': 'application/json',
                        'X-Telegram-Init-Data': initData
                    }),
                    body: JSON.stringify({
                        channel: ctx.channel
                    })
                });

                if (!res.ok) {
                    console.error('❌ Ошибка получения токена подписки:', res.status, res.statusText);
                    if (res.status === 403) {
                        throw new Error('Unauthorized');
                    }
                    throw new Error(`Unexpected status code ${res.status}`);
                }

                const data = await res.json();
                console.log('✅ Получен токен подписки для канала:', ctx.channel);
                return data.token;
            }
        });

        subscriptionRef.current = subscription;

        subscription.on('publication', (ctx) => {
            console.log('📨 RAW WebSocket данные:', JSON.stringify(ctx.data, null, 2));

            const data = ctx.data;

            if (data.events && Array.isArray(data.events)) {
                if (data.events.length > 0) {
                    dispatch(updateEvents({
                        events: data.events,
                        isMailingEnabled: isMailingEnabled
                    }));
                }
            } else if (data.longitude !== undefined && data.type !== undefined) {
                dispatch(updateEvents({
                    events: [data],
                    isMailingEnabled: isMailingEnabled
                }));
            } else {
                console.log('❓ Неизвестный формат данных:', data);
            }
        });
        subscription.on('error', (ctx) => {
            console.error('❌ Ошибка подписки:', ctx);
        });

        subscription.on('subscribed', () => {
            console.log('✅ Подписка на канал активирована');
        });

        subscription.on('unsubscribed', () => {
            console.log('🔕 Подписка отключена');
        });

        // Подключаемся
        subscription.subscribe();
        centrifuge.connect();

        // Функция очистки
        return () => {
            console.log('🧹 Очистка Centrifugo соединения');

            if (subscriptionRef.current) {
                subscriptionRef.current.unsubscribe();
                subscriptionRef.current = null;
            }

            if (centrifugeRef.current) {
                centrifugeRef.current.disconnect();
                centrifugeRef.current = null;
            }

            setIsConnected(false);
        };
    }, [connectionToken, cityTag, isPushEnabled, isMailingEnabled, dispatch]);

    return { isConnected };
};